import { useFetchSpanStatus } from 'client/hooks/span/useSpanStatus';
import { AnchorAPI } from 'client/modules/AnchorAPI';
import { LibraryAudio } from 'client/modules/AnchorAPI/fetchAudioLibrary';
import { Audio } from 'client/modules/AnchorAPI/v3/episodes';
import { WaveformAudioInfo } from 'client/screens/EpisodeScreen/context';
import { useQuery } from 'react-query';
import { FetchEpisodeVideoResponse } from 'client/modules/AnchorAPI/v3/episodes/fetchEpisodeVideo';
import {
  useFetchEpisodeVideo,
  useFetchVideoPreviewStatus,
} from 'hooks/video/useVideo';
import { FetchVideoPreviewStatusResponse } from 'client/modules/AnchorAPI/v3/upload/fetchVideoPreviewStatus';
import { useFetchPodcastMetadata } from 'hooks/podcast/usePodcastMetadata';
import { getQueryStatus } from 'hooks/utils/getQueryStatus';

export const USE_FETCH_EPISODE_AUDIO_KEY = 'useAdInsertion';
type AdInsertionAudioType = WaveformAudioInfo &
  Pick<Audio, 'type' | 'duration'>;

type AdInsertionParams = {
  webEpisodeId?: string;
  libraryAudios?: LibraryAudio[];
};

type AdInsertionAudioFromVideo = WaveformAudioInfo &
  Pick<FetchEpisodeVideoResponse, 'duration'>;

/**
 * this hook will determine if a user can use the waveform UI to insert ads:
 * - is a SPAN user OR Studio creator
 * - episode has a single original audio (e.g. not a transition)
 *
 * the episode audio we use for the ad insertion can come from different places
 * (in order of preference):
 *
 * 1. libraryAudios - this is passed down as an argument to this hook. currently,
 *    this is used when a user is modifying their episode from the episode builder
 *    and are changing the episode segments in the staging area BEFORE the episode
 *    is actually saved. Because these are unsaved changes to the episode, we can't
 *    fetch for the audio from the server since the changes are happening on the
 *    client only
 * 2. audio preview from a video episode - if the episode is a video episode, there
 *    is a special endpoint we need to use to grab the audio version which we can
 *    actually use with the waveform ad insertion UI.
 * 3. audio segments saved to the episode - this would be the "normal" audio one
 *    could save to an episode
 */
export function useAdInsertion({
  webEpisodeId,
  libraryAudios,
}: AdInsertionParams) {
  const { isSPANUser } = useFetchSpanStatus();

  const {
    data: podcastMetadata,
    status: statusFetchPodcastMetadata,
  } = useFetchPodcastMetadata();
  const isStudioCreator = Boolean(podcastMetadata?.isStudioCreator);

  const isWaveformAdInsertionUser = isSPANUser || isStudioCreator;
  const isLibraryAudio = libraryAudios && libraryAudios.length > 0;

  const enableFetchEpisodeVideoQuery =
    !isLibraryAudio && isWaveformAdInsertionUser;
  // TODO: Prevent video fetch in ad insertion hook if episode is NOT video
  // https://anchorfm.atlassian.net/browse/PODRACER-1978
  const {
    data: episodeVideo,
    status: statusEpisodeVideo,
  } = useFetchEpisodeVideo({
    webEpisodeId,
    isEnabled: enableFetchEpisodeVideoQuery,
  });
  const isVideoEpisode =
    statusEpisodeVideo === 'success' &&
    episodeVideo &&
    Object.keys(episodeVideo).length > 0;
  const videoRequestUuid = episodeVideo?.requestUuid;
  const {
    data: videoPreviewStatus,
    status: statusVideoPreviewStatus,
  } = useFetchVideoPreviewStatus({
    requestUuid: videoRequestUuid,
  });

  const video = getAdInsertionAudioFromEpisodeVideo({
    episodeVideo,
    videoPreviewStatus,
  });

  /**
   * only enable the query if:
   * - webEpisodeId is defined
   * - user is allowed to use waveform
   * - libraryAudios have not been provided or there are no audios in the array
   * - episode is not a video podcast
   */
  const enableFetchEpisodeAudioQuery =
    !!webEpisodeId &&
    isWaveformAdInsertionUser &&
    !isLibraryAudio &&
    !isVideoEpisode;

  const { data, status: statusEpisodeAudio } = useQuery(
    [USE_FETCH_EPISODE_AUDIO_KEY, webEpisodeId],
    () => {
      return AnchorAPI.fetchEpisodeAudio({ webEpisodeId: webEpisodeId! });
    },
    { enabled: enableFetchEpisodeAudioQuery }
  );
  const episodeAudios = getEpisodeAudios({
    libraryAudios,
    audios: data?.audios,
    video,
  });
  const isWaveformAdInsertionEpisode = getIsWaveformAdInsertionEpisode({
    audios: episodeAudios,
  });
  const { duration, audioId, url, caption } = episodeAudios[0] || {};

  return {
    isWaveformAdInsertionEpisode,
    isWaveformAdInsertionUser,
    duration,
    audioId,
    url,
    caption,
    status: getQueryStatus([
      statusEpisodeAudio,
      statusEpisodeVideo,
      statusVideoPreviewStatus,
      statusFetchPodcastMetadata,
    ]),
  };
}

/**
 * returns an audio object that can be used with the waveform modal
 */
function getAdInsertionAudioFromEpisodeVideo({
  episodeVideo,
  videoPreviewStatus,
}: {
  episodeVideo?: FetchEpisodeVideoResponse;
  videoPreviewStatus?: FetchVideoPreviewStatusResponse;
}): AdInsertionAudioFromVideo | null {
  if (!episodeVideo || !videoPreviewStatus) {
    return null;
  }

  const { caption, duration, url } = episodeVideo;
  const { webAudioId } = videoPreviewStatus;
  return {
    audioId: webAudioId,
    url,
    caption,
    duration,
  };
}

/**
 * returns a new array of audios derived from audio/video fetched from one of a few places:
 * - audio library via episode builder's staging area
 * - directly fetching the episode audio (via the useQuery in the `useAdInsertion` hook)
 * - video for video podcasters
 */
function getEpisodeAudios({
  libraryAudios,
  audios,
  video,
}: {
  libraryAudios?: LibraryAudio[];
  audios?: Audio[];
  video: AdInsertionAudioFromVideo | null;
}): AdInsertionAudioType[] {
  if (libraryAudios?.length) {
    return libraryAudios.map(libraryAudio => {
      const { duration, audioId, caption, url, type } = libraryAudio;
      return { duration, audioId, caption, url: url || undefined, type };
    });
  }
  if (video) {
    const { duration, audioId, caption, url } = video;
    return [
      {
        type: 'default', // matching the type to user generated audios
        duration,
        audioId,
        caption,
        url,
      },
    ];
  }
  if (audios?.length) {
    return audios.map(audio => {
      const { duration, audioId, caption, urls, type } = audio;
      const { url } = urls ? urls[0] : { url: undefined };
      return {
        duration,
        audioId,
        caption,
        url,
        type,
      };
    });
  }
  return [];
}

function getIsWaveformAdInsertionEpisode({
  audios,
}: {
  audios: AdInsertionAudioType[];
}) {
  // if no audio or more than one audio, user can't use waveform
  if (audios.length === 0 || audios.length > 1) return false;

  // if audio is user generated, user can use waveform
  if (
    !['ad', 'interlude', 'music', 'shoutout', 'callin'].includes(audios[0].type)
  )
    return true;

  // fallback to false
  return false;
}
